<template>
  <div>
    <!-- Filters -->
    <b-card no-body>
      <b-card-header class="pb-50">
        <h5 class="mb-1">
          Filtros
        </h5>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col
            :md="isStagingKpiEnvironment ? 4 : 6"
            cols="12"
          >
            <b-form-group
              label="Período"
              label-for="searchReportRange"
            >
              <flat-pickr
                id="searchReportRange"
                v-model="rangeDate"
                class="form-control"
                :config="flatPickrConfig"
                placeholder="Informe um período para consulta do KPI"
              />
            </b-form-group>
          </b-col>
          <b-col
            :md="isStagingKpiEnvironment ? 4 : 6"
            cols="12"
          >
            <b-form-group
              label="PN"
              label-for="searchConsultant"
            >
              <v-select
                id="searchConsultant"
                v-model="consultant"
                :options="consultants"
                :loading="loading_consultants"
                :close-on-select="false"
                label="name"
                multiple
              >
                <template #no-options>
                  Sem resultados
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            v-if="isStagingKpiEnvironment"
            md="4"
            cols="12"
          >
            <b-form-group
              label="Snapshot"
              label-for="searchSnapshotMode"
            >
              <b-form-select
                id="searchSnapshotMode"
                v-model="snapshotMode"
                :options="snapshotModeOptions"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="align-items-end mt-1">
          <b-col cols="12" class="text-right">
            <b-row class="align-items-center justify-content-end">
              <b-col v-if="showFiltersButton" cols="auto">
                <b-button
                  variant="outline-secondary"
                  @click.prevent="showFiltersSidebar = true"
                >
                  <feather-icon icon="SlidersIcon" class="mr-50" />
                  <span>Filtros</span>
                </b-button>
              </b-col>
              <b-col cols="auto">
                <b-button
                  variant="outline-primary"
                  :disabled="loading"
                  @click.prevent="search"
                >
                  <b-spinner
                    v-if="loading"
                    small
                  />
                  <feather-icon
                    v-else
                    icon="SearchIcon"
                    class="mr-50"
                  />
                  <span>
                    {{ loading ? "Aguarde..." : "Pesquisar" }}
                  </span>
                </b-button>
              </b-col>
            </b-row>
          </b-col>
        </b-row>

      </b-card-body>
    </b-card>

    <kpi-filters-sidebar
      :visible.sync="showFiltersSidebar"
      :contract-status="contractStatus"
      :all-consultants="allConsultants"
      :include-inactive-franchises="includeInactiveFranchises"
      :include-inactive-networks="includeInactiveNetworks"
      :disable-contract-networks="disableContractNetworks"
      :include-employees="includeEmployees"
      :snapshot-mode="snapshotMode"
      @save="saveFilters"
    />

    <!-- Table Container Card -->
    <template v-if="totalizers.length > 0">
      <kpi-totalizers
        :totalizers="totalizers"
        @changeVisibleFranchise="changeFranchiseVisibility"
        @rowClicked="changeFocus"
      />
    </template>

    <template v-for="(franchise, leader_franchise_name) in franchises">
      <kpi-table
        v-if="getFranchiseVisibility(leader_franchise_name)"
        :key="leader_franchise_name"
        :ref="leader_franchise_name"
        :franchise-data="franchise"
        :franchise-name="leader_franchise_name"
        :total="getFranchiseTotal(leader_franchise_name)"
      />
    </template>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BBadge,
  BCardHeader,
  BCardBody,
  BSpinner,
  BFormGroup,
  BFormSelect,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import flatPickr from 'vue-flatpickr-component'
import ShortcutButtonsPlugin from 'shortcut-buttons-flatpickr'
import todayButton from '@/helpers/todayButton'
import { isPnRole, isFounderRole } from '@/constants/auth'
import * as accountTypes from '@/modules/account/store/types'
import { Portuguese } from 'flatpickr/dist/l10n/pt.js'
import * as sharedTypes from '@/modules/shared/store/types'
import * as types from '../store/types'
import KpiTable from '../components/KpiTable'
import KpiTotalizers from '../components/KpiTotalizers'
import KpiFiltersSidebar from '../components/KpiFiltersSidebar'

const KPI_SANITY_FIELDS = [
  'leads_quantity',
  'hot_leads_quantity',
  'scheduled_interview_quantity',
  'pre_interviews_quantity',
  'interviews_quantity',
  'done_interview_quantity',
  'interview_positive_feedbacks_quantity',
  'client_leads_quantity',
  'consultant_leads_quantity',
  'scheduled_x_moment_quantity',
  'done_x_moment_quantity',
  'x_moment_positive_feedbacks_quantity',
  'scheduled_partnership_quantity',
  'done_partnership_quantity',
  'partnership_positive_feedbacks_quantity',
  'scheduled_negotiation_quantity',
  'done_negotiation_quantity',
  'negotiation_positive_feedbacks_quantity',
  'customers_quantity',
  'interview_contract_quantity',
  'negotiation_interview_contract_quantity',
  'other_financial_solutions_contract_quantity',
  'subscription_contract_quantity',
  'renewal_contract_quantity',
  'support_contract_quantity',
  'signature_contract_quantity',
  'interview_contract_amount',
  'other_financial_solutions_contract_amount',
  'subscription_contract_amount',
  'renewal_contract_amount',
  'support_contract_amount',
  'signature_contract_amount',
  'total_contract_amount',
  'subscription_revenue_reconciled',
  'renewal_revenue_reconciled',
]

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BBadge,
    BSpinner,
    BFormGroup,
    BFormSelect,
    KpiFiltersSidebar,
    vSelect,
    flatPickr,
    KpiTable,
    KpiTotalizers,
  },
  setup() {
    return { toast: useToast() }
  },
  data() {
    return {
      loading: false,
      loading_consultants: false,
      contractStatus: 'valid',
      allConsultants: false,
      includeInactiveFranchises: false,
      includeInactiveNetworks: false,
      disableContractNetworks: false,
      includeEmployees: false,
      showFiltersSidebar: false,
      flatPickrConfig: {
        altFormat: 'd/m/Y',
        altInput: true,
        mode: 'range',
        locale: Portuguese,
        plugins: [ShortcutButtonsPlugin(todayButton)],
      },
      consultant: [],
      rangeDate: undefined,
      franchises: {},
      franchiseVisibilities: [],
      snapshotMode: 'live',
      snapshotCheckLoading: false,
      snapshotCheckResult: null,
      snapshotModeOptions: [
        { value: 'auto', text: 'Materializado + live' },
        { value: 'live', text: 'Live' },
        { value: 'snapshot', text: 'Materializado' },
      ],
    }
  },
  computed: {
    ...mapGetters({
      kpiList: types.KPI_LIST,
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
      consultants: types.CONSULTANTS,
      user: accountTypes.USER,
    }),
    isStagingKpiEnvironment() {
      const envName = String(process.env.VUE_APP_ENV_NAME || '').toLowerCase()
      const kpiEndpoint = String(process.env.VUE_APP_KPI_ENDPOINT || '').toLowerCase()
      const hostname = String(window.location.hostname || '').toLowerCase()
      return (
        envName === 'staging'
        || kpiEndpoint.includes('staging.')
        || hostname.includes('staging.')
      )
    },
    isPn() {
      return isPnRole(this.user?.user_contract_type_id)
    },
    isFounder() {
      return isFounderRole(this.user?.user_role_id)
    },
    showFiltersButton() {
      return !this.isPn || this.isFounder
    },
    showUncategorized() {
      return this.allConsultants && this.includeInactiveFranchises && this.includeInactiveNetworks
    },
    totalizers() {
      return _(this.kpiList)
        .filter(item => this.showUncategorized || !!item.leader_franchise_name)
        .groupBy(item => item.leader_franchise_name || 'Não categorizado')
        .map((objs, key) => ({
          leader_franchise_name: key,
          consultants_quantity: objs.length,
          name: 'Total',
          hot_leads_quantity: _.sumBy(objs, 'hot_leads_quantity'),
          scheduled_interview_quantity: _.sumBy(
            objs,
            'scheduled_interview_quantity',
          ),
          pre_interviews_quantity: _.sumBy(objs, 'pre_interviews_quantity'),
          customers_quantity: _.sumBy(objs, 'customers_quantity'),
          interview_contract_quantity: _.sumBy(
            objs,
            'interview_contract_quantity',
          ),
          negotiation_interview_contract_quantity: _.sumBy(
            objs,
            'negotiation_interview_contract_quantity',
          ),
          done_interview_quantity: _.sumBy(objs, 'done_interview_quantity'),
          client_leads_quantity: _.sumBy(objs, 'client_leads_quantity'),
          consultant_leads_quantity: _.sumBy(objs, 'consultant_leads_quantity'),
          scheduled_x_moment_quantity: _.sumBy(
            objs,
            'scheduled_x_moment_quantity',
          ),
          done_x_moment_quantity: _.sumBy(objs, 'done_x_moment_quantity'),
          x_moment_positive_feedbacks_quantity: _.sumBy(
            objs,
            'x_moment_positive_feedbacks_quantity',
          ),
          scheduled_partnership_quantity: _.sumBy(
            objs,
            'scheduled_partnership_quantity',
          ),
          done_partnership_quantity: _.sumBy(objs, 'done_partnership_quantity'),
          partnership_positive_feedbacks_quantity: _.sumBy(
            objs,
            'partnership_positive_feedbacks_quantity',
          ),
          interview_contract_amount: _.sumBy(objs, 'interview_contract_amount'),
          subscription_contract_amount: _.sumBy(
            objs,
            item => Number(item.subscription_contract_amount) || 0,
          ),
          subscription_revenue_reconciled: _.sumBy(
            objs,
            item => Number(item.subscription_revenue_reconciled) || 0,
          ),
          renewal_contract_amount: _.sumBy(
            objs,
            item => Number(item.renewal_contract_amount) || 0,
          ),
          other_financial_solutions_contract_amount: _.sumBy(
            objs,
            'other_financial_solutions_contract_amount',
          ),
          total_contract_amount: _.sumBy(
            objs,
            item => Number(item.total_contract_amount) || 0,
          ),
          signature_contract_quantity: _.sumBy(
            objs,
            'signature_contract_quantity',
          ),
          support_contract_quantity: _.sumBy(objs, 'support_contract_quantity'),
          scheduled_negotiation_quantity: _.sumBy(
            objs,
            'scheduled_negotiation_quantity',
          ),
          negotiation_positive_feedbacks_quantity: _.sumBy(
            objs,
            'negotiation_positive_feedbacks_quantity',
          ),
          renewal_contract_quantity: _.sumBy(objs, 'renewal_contract_quantity'),
        }))
        .value()
    },
  },
  mounted() {
    this.itemsPerPage = this.initialItemsPerPage
    const currentDate = new Date()
    const firstDayOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1,
    )
      .toISOString()
      .split('T')[0]
    const currentDayOfMonth = currentDate.toISOString().split('T')[0]
    this.rangeDate = `${firstDayOfMonth} até ${currentDayOfMonth}`
    this.loading_consultants = true
    this.getConsultants()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao carregar os consultores para seleção. Entre em contato com o setor de TI.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        this.loading_consultants = false
      })
    this.search()
  },
  methods: {
    ...mapActions({
      getkpiList: types.GET_CONSULTANT_AND_CHILDS,
      getKpiSnapshotRanges: types.GET_KPI_SNAPSHOT_RANGES,
      getConsultants: types.GET_CONSULTANTS,
    }),
    saveFilters({ contractStatus, snapshotMode, allConsultants, includeInactiveFranchises, includeInactiveNetworks, disableContractNetworks, includeEmployees }) {
      this.contractStatus = contractStatus
      this.snapshotMode = snapshotMode
      this.allConsultants = allConsultants
      this.includeInactiveFranchises = includeInactiveFranchises
      this.includeInactiveNetworks = includeInactiveNetworks
      this.disableContractNetworks = disableContractNetworks
      this.includeEmployees = includeEmployees
      this.search()
    },
    search() {
      this.loading = true
      let startDate
      let endDate
      if (this.rangeDate) {
        [startDate, endDate] = this.rangeDate.split(' até ')
      }
      const payload = {
        startDate,
        endDate,
        kpiContractStatus: this.contractStatus,
        snapshotMode: this.snapshotMode,
        allConsultants: false,
        includeInactiveFranchises: false,
        includeInactiveNetworks: false,
        disableContractNetworks: false,
        includeEmployees: false
      }
      if (this.allConsultants) {
        payload.allConsultants = true
      }
      if (this.includeInactiveFranchises) {
        payload.includeInactiveFranchises = true
      }
      if (this.includeInactiveNetworks) {
        payload.includeInactiveNetworks = true
      }
      if (this.disableContractNetworks) {
        payload.disableContractNetworks = true
      }
      if (this.includeEmployees) {
        payload.includeEmployees = true
      }
      this.getkpiList(payload)
        .then(() => {
          const filteredKpiList = this.getFilteredKpiList()
          this.franchises = _.groupBy(
            filteredKpiList,
            item => item.leader_franchise_name || 'Não categorizado',
          )
          this.franchiseVisibilities = _.map(
            Object.keys(this.franchises),
            franchiseName => ({ name: franchiseName, visible: true }),
          )
          return this.compareSnapshotWithLive(payload)
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao recuperar os dados de KPI. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    getFilteredKpiList() {
      let filteredKpiList = []
      if (this.consultant.length) {
        _.forEach(this.consultant, item => {
          filteredKpiList = _.concat(
            filteredKpiList,
            _.filter(this.kpiList, { id: item.id }),
          )
        })
      } else {
        filteredKpiList = this.kpiList
      }
      if (!this.showUncategorized) {
        filteredKpiList = _.filter(filteredKpiList, item => !!item.leader_franchise_name)
      }
      return filteredKpiList
    },
    compareSnapshotWithLive(payload) {
      if (!this.isStagingKpiEnvironment || payload.snapshotMode === 'live') {
        this.snapshotCheckResult = null
        return Promise.resolve()
      }

      this.snapshotCheckLoading = true

      return this.getKpiSnapshotRanges(payload)
        .then(rangesResponse => {
          const ranges = _.get(rangesResponse, 'data.ranges', [])

          if (!ranges.length) {
            this.snapshotCheckResult = {
              matches: true,
              label: 'Sem snapshot',
            }
            return Promise.resolve()
          }

          return Promise.all(
            ranges.map(range => Promise.all([
              this.getkpiList({
                ...payload,
                ...range,
                snapshotMode: 'snapshot',
                skipCommit: true,
              }),
              this.getkpiList({
                ...payload,
                ...range,
                snapshotMode: 'live',
                skipCommit: true,
              }),
            ])),
          ).then(rangeResponses => {
            const differences = _.flatMap(rangeResponses, ([snapshotResponse, liveResponse]) => this.getKpiPayloadDifferences(
              snapshotResponse.data,
              liveResponse.data,
            ))

            this.snapshotCheckResult = {
              matches: differences.length === 0,
              label:
                differences.length === 0
                  ? 'Live OK'
                  : `${differences.length} divergencias`,
            }

            if (differences.length > 0) {
              this.toast({
                component: ToastificationContent,
                props: {
                  title: 'Snapshot divergente',
                  text: 'O resultado materializado nao confere com o live para os dias materializados.',
                  icon: 'AlertTriangleIcon',
                  variant: 'warning',
                },
              })
            }
          })
        })
        .catch(() => {
          this.snapshotCheckResult = {
            matches: false,
            label: 'Live indisponivel',
          }
        })
        .finally(() => {
          this.snapshotCheckLoading = false
        })
    },
    getKpiPayloadDifferences(materializedRows, liveRows) {
      const materializedByKey = _.keyBy(
        materializedRows || [],
        this.getKpiPayloadRowKey,
      )
      const liveByKey = _.keyBy(liveRows || [], this.getKpiPayloadRowKey)
      const rowKeys = _.uniq([
        ...Object.keys(materializedByKey),
        ...Object.keys(liveByKey),
      ])

      return _.filter(rowKeys, rowKey => {
        const materializedRow = materializedByKey[rowKey]
        const liveRow = liveByKey[rowKey]

        if (!materializedRow || !liveRow) {
          return true
        }

        return _.some(KPI_SANITY_FIELDS, field => {
          const materializedValue = Number(materializedRow[field]) || 0
          const liveValue = Number(liveRow[field]) || 0

          return Math.abs(materializedValue - liveValue) > 0.01
        })
      })
    },
    getKpiPayloadRowKey(row) {
      return [
        row.id,
        row.digital_franchise_id || '',
        row.leader_franchise_name || '',
        row.name || '',
      ].join('|')
    },
    changeFocus(item) {
      if (item.leader_franchise_name !== 'Total') {
        const index = _.findIndex(this.franchiseVisibilities, {
          name: item.leader_franchise_name,
        })
        if (this.franchiseVisibilities[index]?.visible) {
          const ref = this.$refs[item.leader_franchise_name][0]
          ref.visible = true
          ref.$el.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          })
        }
      }
    },
    getFranchiseTotal(leaderFranchiseName) {
      if (this.consultant.length) {
        const filteredTotalizer = this.filteredTotalizers(
          this.getFilteredKpiList(),
        )
        const totalizer = _.find(filteredTotalizer, {
          leader_franchise_name: leaderFranchiseName,
        })
        if (totalizer === undefined) {
          return {}
        }
        return totalizer
      }
      return _.find(this.totalizers, {
        leader_franchise_name: leaderFranchiseName,
      })
    },
    changeFranchiseVisibility(payload) {
      const index = _.findIndex(this.franchiseVisibilities, {
        name: payload.name,
      })
      if (
        payload === undefined
        || this.franchiseVisibilities[index]?.visible === undefined
      ) {
        return
      }
      if (payload.name !== 'Total') {
        this.franchiseVisibilities[index].visible = payload.visible
      }
    },
    getFranchiseVisibility(name) {
      const index = _.findIndex(this.franchiseVisibilities, { name })
      return this.franchiseVisibilities[index]?.visible
    },
    filteredTotalizers(filteredKpiList) {
      return _(filteredKpiList)
        .groupBy('leader_franchise_name')
        .map((objs, key) => ({
          leader_franchise_name: key,
          name: 'Total',
          hot_leads_quantity: _.sumBy(objs, 'hot_leads_quantity'),
          scheduled_interview_quantity: _.sumBy(
            objs,
            'scheduled_interview_quantity',
          ),
          pre_interviews_quantity: _.sumBy(objs, 'pre_interviews_quantity'),
          customers_quantity: _.sumBy(objs, 'customers_quantity'),
          interview_contract_quantity: _.sumBy(
            objs,
            'interview_contract_quantity',
          ),
          negotiation_interview_contract_quantity: _.sumBy(
            objs,
            'negotiation_interview_contract_quantity',
          ),
          done_interview_quantity: _.sumBy(objs, 'done_interview_quantity'),
          client_leads_quantity: _.sumBy(objs, 'client_leads_quantity'),
          consultant_leads_quantity: _.sumBy(objs, 'consultant_leads_quantity'),
          scheduled_x_moment_quantity: _.sumBy(
            objs,
            'scheduled_x_moment_quantity',
          ),
          done_x_moment_quantity: _.sumBy(objs, 'done_x_moment_quantity'),
          x_moment_positive_feedbacks_quantity: _.sumBy(
            objs,
            'x_moment_positive_feedbacks_quantity',
          ),
          scheduled_partnership_quantity: _.sumBy(
            objs,
            'scheduled_partnership_quantity',
          ),
          done_partnership_quantity: _.sumBy(objs, 'done_partnership_quantity'),
          partnership_positive_feedbacks_quantity: _.sumBy(
            objs,
            'partnership_positive_feedbacks_quantity',
          ),
          interview_contract_amount: _.sumBy(objs, 'interview_contract_amount'),
          subscription_contract_amount: _.sumBy(
            objs,
            item => Number(item.subscription_contract_amount) || 0,
          ),
          subscription_revenue_reconciled: _.sumBy(
            objs,
            item => Number(item.subscription_revenue_reconciled) || 0,
          ),
          renewal_contract_amount: _.sumBy(
            objs,
            item => Number(item.renewal_contract_amount) || 0,
          ),
          other_financial_solutions_contract_amount: _.sumBy(
            objs,
            'other_financial_solutions_contract_amount',
          ),
          total_contract_amount: _.sumBy(
            objs,
            item => Number(item.total_contract_amount) || 0,
          ),
          signature_contract_quantity: _.sumBy(
            objs,
            'signature_contract_quantity',
          ),
          support_contract_quantity: _.sumBy(objs, 'support_contract_quantity'),
          scheduled_negotiation_quantity: _.sumBy(
            objs,
            'scheduled_negotiation_quantity',
          ),
          negotiation_positive_feedbacks_quantity: _.sumBy(
            objs,
            'negotiation_positive_feedbacks_quantity',
          ),
          renewal_contract_quantity: _.sumBy(objs, 'renewal_contract_quantity'),
        }))
        .value()
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
.switch-mt {
  margin-top: 0.5rem;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>